import { useEffect, useState } from "react";
import { AlertTriangle, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [threshold, setThreshold] = useState("");
  const [inactivityDays, setInactivityDays] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/settings").then(({ data }) => {
      setSettings(data);
      setThreshold(String(data.alert_threshold));
      setInactivityDays(String(data.inactivity_days));
    }).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    const thresholdValue = Number(threshold);
    const daysValue = Number(inactivityDays);
    if (isNaN(thresholdValue) || thresholdValue < 0 || thresholdValue > 100) {
      toast.error("Score alert threshold must be between 0 and 100");
      return;
    }
    if (isNaN(daysValue) || daysValue < 1 || daysValue > 90) {
      toast.error("Inactivity alert must be between 1 and 90 days");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        alert_threshold: thresholdValue,
        alerts_enabled: settings?.alerts_enabled ?? true,
        inactivity_days: daysValue,
      });
      setSettings(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="space-y-8 fade-up max-w-2xl">
        <div>
          <h1 className="font-heading font-light text-3xl tracking-tight">Settings</h1>
          <p className="text-sm text-neutral-500 mt-1">Alert thresholds for this company.</p>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 h-40 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-8 fade-up max-w-2xl">
      <div>
        <h1 className="font-heading font-light text-3xl tracking-tight" data-testid="settings-title">Settings</h1>
        <p className="text-sm text-neutral-500 mt-1">Alert thresholds for this company.</p>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg divide-y divide-neutral-100">
        <div className="px-6 py-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-neutral-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Score alerts</p>
              <p className="text-xs text-neutral-500 mt-0.5">Flag a session on the Overview page when its overall score falls below this percentage.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <input data-testid="score-threshold-input" type="number" min="0" max="100" value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-20 border border-neutral-300 rounded-md px-2.5 py-1.5 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#111]" />
            <span className="text-xs text-neutral-500 w-9">%</span>
          </div>
        </div>

        <div className="px-6 py-5 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Clock3 className="w-4 h-4 text-neutral-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Inactivity alerts</p>
              <p className="text-xs text-neutral-500 mt-0.5">Flag an employee on the Notifications bell after this many days with no audio.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <input data-testid="inactivity-days-input" type="number" min="1" max="90" value={inactivityDays}
              onChange={(e) => setInactivityDays(e.target.value)}
              className="w-20 border border-neutral-300 rounded-md px-2.5 py-1.5 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#111]" />
            <span className="text-xs text-neutral-500 w-9">days</span>
          </div>
        </div>
      </div>

      <button data-testid="save-settings-button" onClick={save} disabled={saving}
        className="bg-[#111] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
        {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}
